import { Box, Grid, GridItem, Stack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import type { SingleValue } from 'chakra-react-select';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDataLoader from '@/components/Atoms/FaceliftDataLoader';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftDatePicker, {
	getTodayInNYTimezone,
} from '@/components/Molecules/FaceliftDatePicker/FaceliftDatePicker';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import Show from '@/components/Molecules/Show/Show';
import {
	BgProcessModule,
	useCreateBgProcess,
} from '@/hooks/service-hooks/bgProcess';
import { useGetInsurances } from '@/hooks/service-hooks/insurances';
import { useGetInventories, useUpdateInventory } from '@/hooks/service-hooks/inventory';
import { useGetPatientsAi } from '@/hooks/service-hooks/patients';
import { usePaginatedSelect } from '@/hooks/usePaginatedSelect';
import { useAuthStore } from '@/store';
import type {
	CreateDenialSupportPayload,
} from '@/types/denialSupport';
import type { Insurance, Patients } from '@/types/patient';
import type { FilterType } from '@/types/table';
import { formatDateToString } from '@/utils/dateUtils';
import type { DenialSupportFormProps } from './types';

/**
 * Form validation schema using Yup
 * Defines required fields and validation rules for the Denial Support form
 */
const denialSupportSchema = yup.object({
	product: yup.array().of(yup.mixed<SelectItem>()).nullable().default(null),
	invoiceNumber: yup.string().trim().default(''),
	invoiceDate: yup.string().trim().default(''),
	denialDate: yup.string().trim().default(''),
	price: yup.number().default(0),
	tissueId: yup.array().of(yup.mixed<SelectItem>()).nullable().default(null),
	patient: yup.mixed<SelectItem>().required('Patient is required'),
	clinic: yup.mixed<SelectItem>().default(null),
	primaryInsurance: yup
		.mixed<SelectItem>()
		.required('Primary insurance is required'),
});

type FormData = yup.InferType<typeof denialSupportSchema>;

const initialDefaultValues: Partial<FormData> = {
	product: null,
	invoiceNumber: '',
	invoiceDate: '',
	denialDate: '',
	price: 0,
	tissueId: null,
	patient: undefined,
	clinic: undefined,
	primaryInsurance: undefined,
};

const CLINIC_KEY = '_ta_clinic_value';
const INVOICE_KEY = 'ta_invoicenumber';

/**
 * DenialSupportForm Component
 *
 * A form component for creating and editing Denial Supports.
 *
 * Features:
 * - Create new Denial Supports or edit existing ones
 * - Multiple file uploads to SharePoint
 * - Auto-population of form fields in edit mode
 * - File management without delete capability
 * - Last uploaded file is saved to the ticket
 *
 * @param mode - 'create' or 'edit' mode
 * @param ticketId - ID of the ticket to edit (required in edit mode)
 * @param onSuccess - Callback function called after successful form submission
 */
const DenialSupportForm: React.FC<DenialSupportFormProps> = ({
	data: { invoiceNumber, clinicId, totalProducts },
	onError,
	onSuccess,
	onIsDirtyChange,
}) => {
	const clinicIdCleaned = clinicId ? clinicId.replace(/[{}]/g, '') : null;

	// Track if date has been initialized by defaultToToday to prevent false dirty state
	const isDateInitializedRef = useRef(false);
	
	// Track if form has been populated to prevent resetting dirty state
	const isFormPopulatedRef = useRef(false);

	const user = useAuthStore((state) => state.user?.userInformation);
	const userId = user?._id || null;

	/**
	 * React Hook Form setup with Yup validation
	 * Manages form state, validation, and submission
	 */
	const {
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		trigger,
		// reset,
	} = useForm<FormData>({
		resolver: yupResolver(denialSupportSchema),
		mode: 'onChange',
		defaultValues: initialDefaultValues,
	});

	// Initialize with DataTable's default values and dynamic clinic filters
	const filters = useMemo<FilterType>(
		() => ({
			page: 1,
			limit: 5000,
			columnsFilters: '[]',
			search: '',
			enabled: true,
			status: '',
			globalFilters: JSON.stringify([
				...(clinicId
					? [
						{
							key: CLINIC_KEY,
							values: [clinicIdCleaned || ''],
						},
					]
					: []),
				...(invoiceNumber
					? [
						{
							key: INVOICE_KEY,
							values: [invoiceNumber],
						},
					]
					: []),
			]),
			sort: '',
		}),
		[clinicIdCleaned, invoiceNumber],
	);
	// Fetch patients for Patient select field
	const patients = usePaginatedSelect({
		queryHook: useGetPatientsAi,
		queryParams: {
			clinicId: clinicIdCleaned,
			enabled: !!clinicIdCleaned,
		},
		transform: (patient: Patients) => ({
			value: patient.contactid,
			label: patient.fullname,
			secondaryInsurance: patient.secondaryInsurance,
			primaryInsurance: patient.primaryInsurance,
			birthdate: patient.birthdate,
			ta_primarymemberid: patient.ta_primarymemberid,
			ta_secondarymemberid: patient.ta_secondarymemberid,
			ta_emrid: patient.ta_emrid,
			firstname: patient.firstname,
			lastname: patient.lastname,
		}),
		errorMessage: 'Unable to load patients',
	});

	// Insurances with pagination
	const primaryInsurances = usePaginatedSelect({
		queryHook: useGetInsurances,
		queryParams: { enabled: true },
		transform: (insurance: Insurance) => ({
			value: insurance.ta_insuranceid,
			label: insurance.ta_name,
		}),
		errorMessage: 'Unable to load insurances',
	});

	const { data, isFetching } = useGetInventories(filters);

	// Mutations for create and update
	const {
		mutate: createDenialSupport,
		isLoading: createDenialSupportLoading,
		isSuccess: createSuccess,
	} = useCreateBgProcess(
		() => {
			// Success callback - background process queued
			setTimeout(() => {
				onSuccess?.();
			}, 2000);
		},
	);

	const { mutateAsync: updateInventorybyDS, isLoading: updateInventoryLoading, isSuccess: updateInventorySuccess } = useUpdateInventory();

	// NOTE: This function is no longer called with background processing.
	// Inventory items linking to denial support needs to be handled post-completion.
	// TODO: Implement inventory linking after bg process completes via:
	// - Post-completion hook in processor, or
	// - Webhook handler, or
	// - Manual linking step in the UI after completion
	const updateInventorybyDenial = async (denialId: string): Promise<any> => {
		const updateShipped = await Promise.all(data?.items.map(item => {
			return updateInventorybyDS({
				inventoryId: item.ta_inventoryid,
				updateData: {
					'ta_DenialSupport@odata.bind': `ta_denialses(${denialId})`,
				},
			});
		}) || [])

		await setTimeout(() => {
			onSuccess?.();
		}, 3000);
		return updateShipped;
	}

	const onFormSubmit = handleSubmit(async (formData) => {

		const payload: CreateDenialSupportPayload =
		{
			'ta_Patient@odata.bind': `contacts(${formData.patient.value})`,
			'ta_PrimaryInsurance@odata.bind': `ta_insurances(${formData.primaryInsurance.value})`,
			ta_denial_date: `${formData.denialDate}`,
			ta_amountbilled: watch('price'),
			'ta_Practice@odata.bind': `accounts(${formData.clinic.value})`,
			ta_invoicenumber: watch('invoiceNumber'),
		};

		createDenialSupport({
			moduleName: BgProcessModule.DENIAL_SUPPORT,
			userId: userId || '',
			clinicId: formData.clinic.value as string,
			payload: {
				payload: payload,
				additionalPayload: {
					clinicName: formData.clinic.label as string,
					inventoryIds: data?.items?.map(item => item.ta_inventoryid).filter(Boolean) || [],
				}
			},
		});
	});

	// Track which fields were auto-populated and should be disabled
	const disabledFields = {
		product: true,
		invoiceNumber: true,
		invoiceDate: true,
		denialDate: false,
		price: true,
		clinic: true,
		tissueId: true,
		patient: false,
		primaryInsurance: false,
	};

	// Aggregate loading, success, and error states from both mutations
	const isLoading = isFetching || createDenialSupportLoading || updateInventoryLoading;
	const isError = false;

	// Auto-populate form fields when data is fetched
	useEffect(() => {
		// Only populate once on initial mount
		if (isFormPopulatedRef.current) return;
		
		if (isLoading) return;

		// Based on the fetched data, Validate fetched orders data before proceeding
		if (!validateFetchedOrdersData() || !data?.items || data.items.length === 0)
			return;

		// Mark as populated to prevent future resets
		isFormPopulatedRef.current = true;

		// If all validations pass, set form values
		const item = data.items[0];
		const totalPrice = data.items.reduce(
			(acc, curr) => acc + (curr.ta_qbamountdue || 0),
			0,
		);

		setValue('invoiceNumber', item?.ta_invoicenumber || '', {
			shouldDirty: false,
		});
		setValue(
			'invoiceDate',
			item?.ta_invoicedate
				? formatDateToString(new Date(item.ta_invoicedate))
				: '',
			{ shouldDirty: false },
		);
		setValue('price', totalPrice, { shouldDirty: false });
		setValue(
			'product',
			data.items.map((item) => ({
				label: item.product?.ta_name || '',
				value: item.product.ta_productid || '',
			})),
			{ shouldDirty: false },
		);
		setValue(
			'tissueId',
			data.items.map((item) => ({
				label: item.ta_tissueid || '',
				value: item.ta_tissueid || '',
			})),
			{ shouldDirty: false },
		);
		setValue(
			'clinic',
			item?.clinic
				? { label: item.clinic.name || '', value: item.clinic.accountid || '' }
				: {
					label: '',
					value: '',
				},
			{ shouldDirty: false },
		);

		// Trigger validation to clear any errors after populating fields
		trigger();
	}, [data, isLoading, setValue, trigger]);

	// Notify parent component when form dirty state changes
	useEffect(() => {
		if (onIsDirtyChange) {
			onIsDirtyChange(!isDirty);
		}
	}, [isDirty, onIsDirtyChange]);

	const validateFetchedOrdersData = () => {
		const inactiveOrderCode = 1;

		// Check if data exists
		if (!data?.items || data.items.length === 0) {
			onError('No Orders found for the provided Invoice Number and Clinic.');
		}

		// Check for total products mismatch
		else if (data.items.length !== totalProducts) {
			onError('One or more Orders are missing for the provided Invoice.');
		}

		// Check for inactive orders
		else if (data.items.find((item) => item.statuscode === inactiveOrderCode)) {
			onError('One or more Orders have an inactive status.');
		}
		// Check for existing denial support
		else if (data.items.find((item) => item?._ta_denialsupport_value)) {
			onError('Denial Support already exists for one or more Orders.');
		}
		// Check invoice due date
		else if (
			data.items.find(
				(item) =>
					item.ta_invoiceduedate &&
					new Date(item.ta_invoiceduedate) < new Date(),
			)
		) {
			onError('One or more Orders have an invoice due date has been passed.');
		}
		// Check for zero or null amount due
		else if (
			data.items.find(
				(item) =>
					item.ta_qbamountdue === 0 ||
					item.ta_qbamountdue === null ||
					item.ta_qbamountdue === undefined,
			)
		) {
			onError('One or more Orders have a zero or null amount due.');
		} else {
			return true;
		}
		return false;
	};

	return (
		<>
			{/* Loading State */}
			<Show when={isLoading && (!createSuccess || !updateInventorySuccess)}>
				<Box
					px={6}
					py={4}
					display="flex"
					justifyContent="center"
					alignItems="center"
					minHeight="400px"
				>
					<FaceliftDataLoader
						title="Loading Invoice details..."
						description="Please wait while we fetch the invoice information."
					/>
				</Box>
			</Show>
			<Show when={!isLoading}>
				<Box px={6} py={4}>
					<Grid templateColumns="repeat(3, 1fr)" gap={4} mb={4}>
						{/* Subject Field - spans 2 columns */}
						<GridItem colSpan={2}>
							<FaceliftSelect<SelectItem, true>
								items={
									data?.items.map((item) => ({
										label: item.product?.ta_name || '',
										value: item.product.ta_productid || '',
									})) || []
								}
								value={(watch('product') || []).filter(
									(item): item is SelectItem => item !== undefined,
								)}
								topLabel="Product *"
								disabled={disabledFields.product}
								isMulti
							/>
						</GridItem>
						<GridItem>
							<FaceliftInput
								topLabel="Invoice Date *"
								error={errors.invoiceDate?.message}
								disabled={disabledFields.invoiceDate}
								value={watch('invoiceDate')}
							/>
						</GridItem>
					</Grid>
					<Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
						<GridItem>
							<FaceliftInput
								topLabel="Invoice Number *"
								error={errors.invoiceNumber?.message}
								disabled={disabledFields.invoiceNumber}
								value={watch('invoiceNumber')}
							/>
						</GridItem>
						<GridItem>
							<FaceliftInput
								topLabel="Price *"
								error={errors.price?.message}
								disabled={disabledFields.price}
								value={watch('price')}
							/>
						</GridItem>
					</Grid>

					<Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
						<GridItem>
							<FaceliftSelect<SelectItem, false>
								topLabel="Clinic"
								placeholder="Select Clinic"
								value={watch('clinic') || null}
								onChange={(newValue) => {
									if (newValue) {
										setValue('clinic', newValue, { shouldDirty: true });
										trigger('clinic');
									}
								}}
								items={[]}
								error={
									typeof errors.clinic?.message === 'string'
										? errors.clinic.message
										: ''
								}
								isSearchable={true}
								required
								isDisabled={disabledFields.clinic}
							/>
						</GridItem>

						<GridItem>
							<FaceliftSelect<SelectItem, true>
								items={
									data?.items.map((item) => ({
										label: item.product?.ta_tissueid || '',
										value: item.product.ta_tissueid || '',
									})) || []
								}
								value={(watch('tissueId') || []).filter(
									(item): item is SelectItem => item !== undefined,
								)}
								topLabel="Tissue Id *"
								disabled={disabledFields.tissueId}
								isMulti
							/>
						</GridItem>
					</Grid>

					<Grid templateColumns="repeat(4, 1fr)" gap={4} mb={4}>
						<GridItem colSpan={2}>
							<FaceliftSelect<SelectItem, false>
								limit={10}
								topLabel="Patient"
								required
								value={watch('patient') as SelectItem}
								onChange={(newValue: SingleValue<SelectItem>) => {
									const patientInfo = patients.items.find(
										(item) => item.value === (newValue as SelectItem)?.value,
									);
									setValue('patient', patientInfo as SelectItem, {
										shouldDirty: true,
									});
									const primaryInsurance = patientInfo?.primaryInsurance;
									setValue(
										'primaryInsurance',
										{
											label: primaryInsurance?.ta_name,
											value: primaryInsurance?.ta_insuranceid,
										},
										{ shouldDirty: true },
									);
									// Trigger validation to clear errors after setting values
									trigger(['patient', 'primaryInsurance']);
								}}
								ai
								aiSuggestionText="Use a keyword like “Smith” to see more relevant results"
								items={patients.items}
								error={typeof errors.patient?.message === 'string' ? errors.patient.message : ''}
								loading={patients.loading}
								placeholder="Type Patient Name"
								onLoadMore={patients.onLoadMore}
							/>
						</GridItem>
						{/* Clinic Field - spans 2 columns */}
						<GridItem colSpan={2}>
							<FaceliftSelect<SelectItem, false>
								limit={15}
								topLabel="Primary Insurance"
								isClearable
								value={watch('primaryInsurance') as SelectItem | null}
								onChange={(newValue: SingleValue<SelectItem>) => {
									setValue(
										'primaryInsurance',
										(newValue as SelectItem) || null,
										{
											shouldDirty: true,
										},
									);
									// Trigger validation to clear/show errors
									trigger('primaryInsurance');
								}}
								required
								items={primaryInsurances.items}
								loading={primaryInsurances.loading}
								error={typeof errors?.primaryInsurance?.message === 'string' ? errors.primaryInsurance.message : ''}
								placeholder="Select Primary Insurance"
								onLoadMore={primaryInsurances.onLoadMore}
								ai
								aiSuggestionText="Use a keyword like “United” to see more relevant results"
							/>
						</GridItem>

						<GridItem colSpan={2}>
							<FaceliftDatePicker
								topLabel="Denial Date"
								required
								placeholder="MM/DD/YYYY"
								value={watch('denialDate')}
								onChange={(date: string) => {
									const currentValue = watch('denialDate');
									
									// Determine if this should mark the form as dirty
									let shouldMarkDirty = true;
									
									// If this is the first time setting a value (initial auto-population from defaultToToday)
									if (!isDateInitializedRef.current) {
										shouldMarkDirty = false;
										isDateInitializedRef.current = true;
									}
									// If the value hasn't actually changed (duplicate call), don't mark as dirty
									else if (currentValue === date) {
										shouldMarkDirty = false;
									}
									
									setValue('denialDate', date, { shouldDirty: shouldMarkDirty });
									// Trigger validation to clear/show errors
									trigger('denialDate');
								}}
								error={errors?.denialDate?.message}
								minDate={getTodayInNYTimezone()}
								defaultToToday
							/>
						</GridItem>
					</Grid>

					{/* Submit/Update Button */}
					<Stack direction="row" justify="flex-end" gap={3}>
						<FaceliftButton
							onClick={onFormSubmit}
							bg="brand.solidGradient"
							color="white"
							width={{ base: 'full', sm: '24', md: '28', lg: '40' }}
							disabled={isLoading}
						>
							Submit
						</FaceliftButton>
					</Stack>
				</Box>

				{/* 
      SUCCESS/ERROR MODAL
      Shows progress and result of form submission
      - Loading state while submitting
      - Success message after successful submission
      - Error message if submission fails
    */}
				<FaceliftSuccessProgressModal
					open={isLoading || createSuccess}
					isLoading={isLoading}
					isSucceed={createSuccess}
					hasError={false}
					onOpenChange={() => { }}
					title={'Denial Support request submitted successfully.'}
					errorMessage={''}
				/>
			</Show>
		</>
	);
};

export default DenialSupportForm;